/**
 * <video-testimonials>
 * Carousel of muted customer videos that advances one card every few seconds,
 * then rewinds to the first card once the last one is in view. Clicking a card
 * opens the video with sound in a player (bottom corner on desktop, full screen
 * on mobile).
 */
if (!customElements.get('video-testimonials')) {
  class VideoTestimonials extends HTMLElement {
    constructor() {
      super();
      // Position is measured in cards, so it survives resizes; render() turns it into pixels.
      this.pos = 0;
      this.stepSize = 0;
      this.maxPos = 0;
      this.elapsed = 0;
      this.tween = null;
      this.pauseReasons = new Set();
      this.drag = null;
      this.suppressClick = false;
      this.current = -1;
      this.opener = null;

      this.onFrame = this.onFrame.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOtherPlayerOpen = this.onOtherPlayerOpen.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-vt-viewport]');
      this.track = this.querySelector('[data-vt-track]');
      this.slides = Array.from(this.track.querySelectorAll('[data-vt-slide]'));
      if (!this.slides.length) return;

      // Editor placeholders are slides without a playable card, so index the cards on their own.
      this.cards = Array.from(this.track.querySelectorAll('[data-vt-open]'));
      this.cards.forEach((card, index) => (card.dataset.vtIndex = index));

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion;
      this.interval = Number(this.dataset.interval) || 4;

      this.setupPlayer();
      this.setupPreviewObserver();
      this.bindSlider();

      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.viewport);
      this.layout();

      this.sectionObserver = new IntersectionObserver(([entry]) => this.togglePause('offscreen', !entry.isIntersecting));
      this.sectionObserver.observe(this);

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('video-testimonials:open', this.onOtherPlayerOpen);

      this.lastTime = performance.now();
      this.frame = requestAnimationFrame(this.onFrame);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.previewObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('video-testimonials:open', this.onOtherPlayerOpen);
      if (this.player) {
        if (!this.player.hidden) document.documentElement.classList.remove('bob-vt-player-open');
        this.playerVideo.pause();
        this.player.remove();
      }
    }

    /* ---------- Slider ---------- */

    layout() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.stepSize = this.slides[0].offsetWidth + gap;
      if (this.stepSize <= 0) return;

      // Furthest position (in cards) where the last card sits flush with the right edge.
      // It can be fractional, e.g. with 2.4 cards visible on mobile.
      const overflow = this.slides.length * this.stepSize - gap - this.viewport.clientWidth;
      this.maxPos = Math.max(0, overflow / this.stepSize);
      this.classList.toggle('is-static', this.maxPos < 0.01);
      this.pos = this.clamp(this.pos);
      this.render();
    }

    clamp(pos) {
      return Math.min(Math.max(pos, 0), this.maxPos);
    }

    bindSlider() {
      this.querySelector('[data-vt-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-vt-next]')?.addEventListener('click', () => this.step(1));

      this.viewport.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.togglePause('hover', true));
      this.viewport.addEventListener('pointerleave', () => this.togglePause('hover', false));
      // Keyboard focus pauses the strip so the focused card stays put; mouse clicks don't.
      this.viewport.addEventListener('focusin', (event) => this.togglePause('focus', event.target.matches(':focus-visible')));
      this.viewport.addEventListener('focusout', () => this.togglePause('focus', false));

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        this.drag = { id: event.pointerId, startX: event.clientX, startPos: this.pos, moved: false };
      });
      this.viewport.addEventListener('pointermove', (event) => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const dx = event.clientX - this.drag.startX;
        if (!this.drag.moved && Math.abs(dx) > 6) {
          this.drag.moved = true;
          this.tween = null;
          this.togglePause('drag', true);
          this.viewport.classList.add('is-dragging');
          this.viewport.setPointerCapture(event.pointerId);
        }
        if (this.drag.moved) {
          // Resist dragging past either end.
          const raw = this.drag.startPos - dx / this.stepSize;
          const clamped = this.clamp(raw);
          this.pos = clamped + (raw - clamped) * 0.3;
          this.render();
        }
      });
      const endDrag = (event) => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        if (this.drag.moved) {
          this.suppressClick = true;
          setTimeout(() => (this.suppressClick = false), 0);
          this.viewport.classList.remove('is-dragging');
          this.togglePause('drag', false);
          // Snap to a whole card, leaning towards the swipe direction once it passes 15% of a card.
          const moved = this.pos - this.drag.startPos;
          const target = Math.abs(moved) < 0.15 ? Math.round(this.pos) : moved > 0 ? Math.ceil(this.pos) : Math.floor(this.pos);
          this.animateTo(this.clamp(target));
        }
        this.drag = null;
      };
      this.viewport.addEventListener('pointerup', endDrag);
      this.viewport.addEventListener('pointercancel', endDrag);

      this.track.addEventListener('click', (event) => {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const card = event.target.closest('[data-vt-open]');
        if (card) this.openPlayer(Number(card.dataset.vtIndex), card);
      });
    }

    step(direction) {
      if (this.maxPos < 0.01) return;
      // Build on an in-flight move so quick repeated clicks each advance one card.
      const base = this.tween ? this.tween.to : this.pos;
      const atEnd = base >= this.maxPos - 0.01;
      const atStart = base <= 0.01;

      // Past the last card rewind to the first; before the first jump to the end.
      if (direction > 0 && atEnd) return this.animateTo(0, 900);
      if (direction < 0 && atStart) return this.animateTo(this.maxPos, 900);

      const next = direction > 0 ? Math.floor(base + 0.01) + 1 : Math.ceil(base - 0.01) - 1;
      this.animateTo(this.clamp(next));
    }

    animateTo(target, duration = 600) {
      this.elapsed = 0;
      this.tween = { from: this.pos, to: target, start: performance.now(), duration };
    }

    togglePause(reason, paused) {
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);
    }

    onFrame(now) {
      const dt = Math.min((now - this.lastTime) / 1000, 0.1);
      this.lastTime = now;

      if (this.tween) {
        const progress = Math.min((now - this.tween.start) / this.tween.duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.pos = this.tween.from + (this.tween.to - this.tween.from) * eased;
        if (progress === 1) this.tween = null;
        this.render();
      } else if (this.autoplay && !this.pauseReasons.size && this.maxPos >= 0.01) {
        this.elapsed += dt;
        if (this.elapsed >= this.interval) this.step(1);
      }

      this.frame = requestAnimationFrame(this.onFrame);
    }

    render() {
      if (this.stepSize <= 0) return;
      this.track.style.transform = `translate3d(${-this.pos * this.stepSize}px, 0, 0)`;
    }

    setupPreviewObserver() {
      // Only decode the muted previews that are actually on screen.
      this.previewObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) target.play().catch(() => {});
            else target.pause();
          });
        },
        { threshold: 0.05 }
      );
      this.track.querySelectorAll('video').forEach((video) => this.previewObserver.observe(video));
    }

    onVisibilityChange() {
      this.togglePause('hidden', document.hidden);
    }


    /* ---------- Floating player ---------- */

    setupPlayer() {
      this.player = this.querySelector('[data-vt-player]');
      this.playerVideo = this.player.querySelector('[data-vt-player-video]');
      this.playerCaption = this.player.querySelector('[data-vt-player-caption]');
      this.muteButton = this.player.querySelector('[data-vt-mute]');

      // Live on <body> so no transformed ancestor can break position: fixed.
      this.player.dataset.vtOwner = this.closest('[data-block-id]')?.dataset.blockId || '';
      this.player.style.cssText = this.closest('.bob-vt')?.getAttribute('style') || '';
      document.body.appendChild(this.player);

      this.player.querySelector('[data-vt-close]').addEventListener('click', () => this.closePlayer());
      this.player.querySelector('[data-vt-player-prev]').addEventListener('click', () => this.showVideo(this.current - 1));
      this.player.querySelector('[data-vt-player-next]').addEventListener('click', () => this.showVideo(this.current + 1));
      this.muteButton.addEventListener('click', () => this.setMuted(!this.playerVideo.muted));
      this.player.querySelector('[data-vt-share]')?.addEventListener('click', () => this.share());
      // Tap toggles pause; on the full-screen mobile player a vertical swipe changes video.
      let swipe = null;
      this.playerVideo.addEventListener('pointerdown', (event) => (swipe = { x: event.clientX, y: event.clientY }));
      this.playerVideo.addEventListener('pointerup', (event) => {
        if (!swipe) return;
        const dx = event.clientX - swipe.x;
        const dy = event.clientY - swipe.y;
        swipe = null;
        if (Math.abs(dy) > 50 && Math.abs(dy) > Math.abs(dx)) {
          this.showVideo(this.current + (dy < 0 ? 1 : -1));
        } else if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          if (this.playerVideo.paused) this.playerVideo.play().catch(() => {});
          else this.playerVideo.pause();
        }
      });
      this.playerVideo.addEventListener('pointercancel', () => (swipe = null));
    }

    openPlayer(index, opener) {
      this.opener = opener;
      document.dispatchEvent(new CustomEvent('video-testimonials:open', { detail: { source: this } }));
      this.player.hidden = false;
      document.documentElement.classList.add('bob-vt-player-open');
      this.showVideo(index);
      this.player.querySelector('[data-vt-close]').focus({ preventScroll: true });
    }

    showVideo(index) {
      const count = this.cards.length;
      if (!count) return;
      this.current = ((index % count) + count) % count;
      const card = this.cards[this.current];
      if (!card) return;

      this.playerVideo.src = card.dataset.src;
      if (card.dataset.poster) this.playerVideo.poster = card.dataset.poster;
      else this.playerVideo.removeAttribute('poster');
      this.playerCaption.textContent = card.dataset.caption || '';

      this.setMuted(false);
      this.playerVideo.play().catch(() => {
        // Browser refused sound without a fresh gesture; fall back to muted playback.
        this.setMuted(true);
        this.playerVideo.play().catch(() => {});
      });
    }

    closePlayer() {
      if (this.player.hidden) return;
      this.playerVideo.pause();
      this.playerVideo.removeAttribute('src');
      this.playerVideo.load();
      this.player.hidden = true;
      document.documentElement.classList.remove('bob-vt-player-open');
      this.current = -1;
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    setMuted(muted) {
      this.playerVideo.muted = muted;
      this.player.classList.toggle('is-muted', muted);
      this.muteButton.setAttribute('aria-pressed', String(muted));
      this.muteButton.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }

    async share() {
      const url = window.location.href;
      try {
        if (navigator.share) {
          await navigator.share({ title: document.title, url });
        } else {
          await navigator.clipboard.writeText(url);
          this.toast('Link copied');
        }
      } catch (error) {
        // User dismissed the share sheet or clipboard is unavailable.
      }
    }

    toast(message) {
      const toast = document.createElement('div');
      toast.className = 'bob-vt-player__toast';
      toast.setAttribute('role', 'status');
      toast.textContent = message;
      this.player.appendChild(toast);
      setTimeout(() => toast.remove(), 1600);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && !this.player.hidden) this.closePlayer();
    }

    onOtherPlayerOpen(event) {
      if (event.detail.source !== this) this.closePlayer();
    }
  }

  customElements.define('video-testimonials', VideoTestimonials);
}
